import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Accordion, Alert, Badge, Button, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import axios from 'axios';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiCopy,
  FiDownload,
  FiExternalLink,
  FiFileText,
  FiSearch,
} from 'react-icons/fi';
import Card from '../../../components/Card';
import { useLanguage } from '../../../i18n/LanguageContext';
import apiUrl from '../../../config/api';
import SearchablePdfPreview, { FindState } from './SearchablePdfPreview';
import ToolShell from './ToolShell';

const normalizeSearchText = (value = '') => String(value || '')
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

const formatFileSize = (bytes = 0) => {
  if (!bytes) {
    return '0 KB';
  }

  if (bytes >= 1024 * 1024) {
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  }

  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
};

const recognizedTextPageSize = 120;

const getCandidateFamily = (mode = '') => {
  if (String(mode || '').startsWith('mininterno')) {
    return 'mininterno';
  }

  if (String(mode || '').startsWith('ecampus')) {
    return 'ecampus';
  }

  return 'generic';
};

const PdfRepairTool = () => {
  const { t } = useLanguage();
  const text = t.toolsPage.pdfRepair;
  const token = localStorage.getItem('token') || sessionStorage.getItem('token');
  const fileInputRef = useRef(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [result, setResult] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [pdfSearchQuery, setPdfSearchQuery] = useState('');
  const [pdfSearchTerm, setPdfSearchTerm] = useState('');
  const [pdfSearchRequest, setPdfSearchRequest] = useState({ id: 0, direction: 'next' });
  const [pdfSearchState, setPdfSearchState] = useState({ matches: { current: 0, total: 0 }, state: null, term: '' });
  const [searchQuery, setSearchQuery] = useState('');
  const [visibleRecognizedLines, setVisibleRecognizedLines] = useState(recognizedTextPageSize);
  const [isLoading, setIsLoading] = useState(false);
  const [isPreparingPdf, setIsPreparingPdf] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const recognizedLines = useMemo(() => (
    String(result?.repairText || '').split('\n').filter(Boolean)
  ), [result]);

  const authHeaders = useMemo(() => ({
    Authorization: `Bearer ${token}`,
    Accept: 'application/json',
  }), [token]);

  const previewLines = useMemo(() => {
    const normalizedQuery = normalizeSearchText(searchQuery.trim());

    if (!normalizedQuery) {
      return recognizedLines.slice(0, visibleRecognizedLines);
    }

    return recognizedLines.filter((line) => normalizeSearchText(line).includes(normalizedQuery));
  }, [recognizedLines, searchQuery, visibleRecognizedLines]);

  const selectedCandidate = useMemo(() => (
    result?.candidates?.find((candidate) => candidate.mode === result.mode) || null
  ), [result]);
  const relevantCandidates = useMemo(() => {
    if (!result?.candidates?.length) {
      return [];
    }

    const selectedFamily = getCandidateFamily(result.mode);
    const familyCandidates = result.candidates.filter((candidate) => getCandidateFamily(candidate.mode) === selectedFamily);

    return (familyCandidates.length ? familyCandidates : result.candidates)
      .filter((candidate) => (
        candidate.mode === result.mode
        || Number(candidate.characters || 0) > 0
        || Number(candidate.questionCount || candidate.questionMarkers || 0) > 0
        || Number(candidate.artifactCount || 0) > 0
      ));
  }, [result]);

  const questionCount = result?.questionMarkers
    || result?.parser?.ecampus?.detected?.total
    || result?.parser?.mininterno?.detected?.total
    || 0;
  const hasIssues = Boolean(result?.issues?.length);
  const pdfSearchFeedback = useMemo(() => {
    if (!pdfSearchState.term) {
      return '';
    }

    if (pdfSearchState.state === FindState.PENDING) {
      return text.pdfSearching;
    }

    if (pdfSearchState.state === FindState.NOT_FOUND && !pdfSearchState.matches?.total) {
      return text.pdfSearchNoResults;
    }

    return text.pdfSearchResultCount(
      pdfSearchState.matches?.current || 0,
      pdfSearchState.matches?.total || 0
    );
  }, [pdfSearchState, text]);

  useEffect(() => () => {
    if (pdfPreview?.url) {
      URL.revokeObjectURL(pdfPreview.url);
    }
  }, [pdfPreview]);

  const handleAnalyzePdf = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!selectedFile) {
      setError(text.choosePdf);
      return;
    }

    const formData = new FormData();
    formData.append('pdf', selectedFile);
    setIsLoading(true);
    setIsPreparingPdf(false);
    setPdfPreview(null);
    setPdfSearchQuery('');
    setPdfSearchTerm('');
    setPdfSearchRequest({ id: 0, direction: 'next' });
    setPdfSearchState({ matches: { current: 0, total: 0 }, state: null, term: '' });
    setVisibleRecognizedLines(recognizedTextPageSize);

    try {
      const response = await axios.post(`${apiUrl}/tools/repairPdfSearch`, formData, {
        headers: {
          ...authHeaders,
          'Content-Type': 'multipart/form-data',
        },
      });

      setResult(response.data);
      setSearchQuery('');
      setIsLoading(false);
      setIsPreparingPdf(true);
      await createPdfPreview(selectedFile, response.data);
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || text.error);
    } finally {
      setIsLoading(false);
      setIsPreparingPdf(false);
    }
  };

  const handleFileChange = (event) => {
    setSelectedFile(event.target.files?.[0] || null);
    setResult(null);
    setPdfPreview(null);
    setPdfSearchQuery('');
    setPdfSearchTerm('');
    setPdfSearchRequest({ id: 0, direction: 'next' });
    setPdfSearchState({ matches: { current: 0, total: 0 }, state: null, term: '' });
    setSearchQuery('');
    setVisibleRecognizedLines(recognizedTextPageSize);
    setError('');
    setSuccess('');
  };

  const handleCopy = async () => {
    if (!result?.repairText) {
      return;
    }

    try {
      await navigator.clipboard.writeText(result.repairText);
      setSuccess(text.copied);
    } catch (copyError) {
      setError(text.copyError);
    }
  };

  const getRepairMethodLabel = (method, fallbackLabel = '') => (
    text.repairMethods?.[method] || fallbackLabel || method || '-'
  );

  const decodeHeaderValue = (value = '') => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };

  const getDownloadFileName = (contentDisposition = '') => {
    const fileNameMatch = String(contentDisposition || '').match(/filename="?([^"]+)"?/i);

    if (fileNameMatch?.[1]) {
      return fileNameMatch[1];
    }

    const cleanFileName = String(result?.fileName || selectedFile?.name || 'pdf-riparato.pdf').replace(/\.pdf$/i, '');
    return `${cleanFileName}-ricercabile.pdf`;
  };

  const createPdfPreview = async (file, analysisResult = result) => {
    const formData = new FormData();
    formData.append('pdf', file);

    const response = await axios.post(`${apiUrl}/tools/repairPdfSearch/pdf`, formData, {
      headers: {
        ...authHeaders,
        'Content-Type': 'multipart/form-data',
      },
      responseType: 'blob',
      timeout: 90000,
    });
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const responseMethod = response.headers?.['x-quizify-repair-method'] || analysisResult?.repairPlan?.method || '';
    const responseLabel = response.headers?.['x-quizify-repair-label']
      ? decodeHeaderValue(response.headers['x-quizify-repair-label'])
      : analysisResult?.repairPlan?.label || '';

    const previewData = {
      blob,
      url: URL.createObjectURL(blob),
      fileName: getDownloadFileName(response.headers?.['content-disposition']),
      method: responseMethod,
      label: getRepairMethodLabel(responseMethod, responseLabel),
      searchTerm: '',
    };

    setPdfPreview(previewData);
    return previewData;
  };

  const handleDownload = async () => {
    if (!selectedFile || !result?.repairText) {
      return;
    }

    setError('');
    setSuccess('');
    setIsDownloading(true);

    try {
      const generatedPdf = pdfPreview?.blob
        ? null
        : await createPdfPreview(selectedFile);
      const currentPdf = pdfPreview?.blob
        ? pdfPreview
        : generatedPdf;
      const downloadUrl = currentPdf.url;
      const link = document.createElement('a');

      link.href = downloadUrl;
      link.download = currentPdf?.fileName || getDownloadFileName();
      link.click();

      if (!currentPdf?.url) {
        URL.revokeObjectURL(downloadUrl);
      }

      setSuccess(text.downloaded);
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || text.downloadError);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleOpenPdf = async () => {
    if (!selectedFile || !result?.repairText) {
      return;
    }

    try {
      setError('');

      if (!pdfPreview?.url) {
        setIsPreparingPdf(true);
        const generatedPdf = await createPdfPreview(selectedFile, result);
        const openedWindow = window.open(generatedPdf.url, '_blank');
        if (!openedWindow) {
          setError(text.openPdfError);
        }
        setIsPreparingPdf(false);
        return;
      }

      const openedWindow = window.open(pdfPreview.url, '_blank');
      if (!openedWindow) {
        setError(text.openPdfError);
      }
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || text.downloadError);
      setIsPreparingPdf(false);
    }
  };

  const handleChooseAnother = () => {
    setResult(null);
    setError('');
    setSuccess('');
    setSearchQuery('');
    setVisibleRecognizedLines(recognizedTextPageSize);
    setPdfSearchQuery('');
    setPdfSearchTerm('');
    setPdfSearchRequest({ id: 0, direction: 'next' });
    setPdfSearchState({ matches: { current: 0, total: 0 }, state: null, term: '' });
    setSelectedFile(null);

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRecognizedTextScroll = (event) => {
    if (searchQuery.trim()) {
      return;
    }

    const target = event.currentTarget;
    const distanceFromBottom = target.scrollHeight - target.scrollTop - target.clientHeight;

    if (distanceFromBottom > 80 || visibleRecognizedLines >= recognizedLines.length) {
      return;
    }

    setVisibleRecognizedLines((count) => Math.min(count + recognizedTextPageSize, recognizedLines.length));
  };

  const requestPdfSearch = (direction = 'next') => {
    const searchTerm = pdfSearchQuery.trim();

    setPdfSearchTerm(searchTerm);
    setPdfSearchRequest((request) => ({
      id: request.id + 1,
      direction,
    }));

    if (!searchTerm) {
      setPdfSearchState({ matches: { current: 0, total: 0 }, state: null, term: '' });
    }
  };

  const handlePdfSearchSubmit = (event) => {
    event.preventDefault();
    requestPdfSearch('next');
  };

  return (
    <ToolShell
      title={text.title}
      subtitle={text.subtitle}
      icon={<FiFileText aria-hidden="true" />}
    >
      <Row className="g-3">
        <Col lg={6}>
          <Card className="quizify-tool-panel h-100">
            <Card.Body>
              {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                  {error}
                </Alert>
              )}
              {success && (
                <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                  {success}
                </Alert>
              )}

              <Form onSubmit={handleAnalyzePdf}>
                <Form.Group className="mb-3">
                  <Form.Label>{text.fileLabel}</Form.Label>
                  <Form.Control
                    ref={fileInputRef}
                    className="quizify-file-input"
                    type="file"
                    accept="application/pdf,.pdf"
                    onChange={handleFileChange}
                  />
                </Form.Group>

                {selectedFile && (
                  <div className="quizify-file-chip mb-3">
                    <FiFileText aria-hidden="true" />
                    <span>{selectedFile.name}</span>
                    <small>{formatFileSize(selectedFile.size)}</small>
                  </div>
                )}

                <div className="d-flex flex-wrap gap-2">
                  <Button type="submit" variant="primary" disabled={isLoading || !token}>
                    {isLoading ? (
                      <>
                        <Spinner animation="border" size="sm" />
                        <span>{text.analyzing}</span>
                      </>
                    ) : (
                      <>
                        <FiSearch aria-hidden="true" />
                        <span>{text.analyze}</span>
                      </>
                    )}
                  </Button>
                  {result && (
                    <Button type="button" variant="soft-secondary" onClick={handleChooseAnother}>
                      {text.reset}
                    </Button>
                  )}
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={6}>
          {result && !isPreparingPdf && (
            <Card className="quizify-tool-panel h-100">
              <Card.Body>
                <div className="quizify-repair-summary">
                  <div className={`quizify-repair-status ${hasIssues ? 'is-warning' : 'is-ready'}`}>
                    <div>
                      {hasIssues ? (
                        <FiAlertTriangle aria-hidden="true" />
                      ) : (
                        <FiCheckCircle aria-hidden="true" />
                      )}
                    </div>
                    <div>
                      <strong>{hasIssues ? text.reviewNeeded : text.readyTitle}</strong>
                      {!result.issues?.length && (
                        <span>{text.cleanResult}</span>
                      )}
                    </div>
                    <div className="quizify-repair-status-actions">
                      <Button
                        type="button"
                        variant="success"
                        size="sm"
                        onClick={handleDownload}
                        disabled={isDownloading || isPreparingPdf}
                      >
                        {isDownloading ? (
                          <Spinner animation="border" size="sm" />
                        ) : (
                          <FiDownload aria-hidden="true" />
                        )}
                        <span>{isDownloading ? text.downloading : text.download}</span>
                      </Button>
                      <Button
                        type="button"
                        variant="soft-primary"
                        size="sm"
                        onClick={handleOpenPdf}
                        disabled={isDownloading || isPreparingPdf}
                      >
                        <FiExternalLink aria-hidden="true" />
                        <span>{text.openPdf}</span>
                      </Button>
                    </div>
                  </div>

                  <div className="quizify-tool-stat-grid">
                    <div>
                      <span>{text.mode}</span>
                      <strong>{getRepairMethodLabel(result.repairPlan?.method, result.repairPlan?.label)}</strong>
                    </div>
                    <div>
                      <span>{text.questions}</span>
                      <strong>{questionCount}</strong>
                    </div>
                    <div>
                      <span>{text.characters}</span>
                      <strong>{result.extractedCharacters || 0}</strong>
                    </div>
                    <div>
                      <span>{text.artifacts}</span>
                      <strong>{result.artifactCount || 0}</strong>
                    </div>
                  </div>

                  {!!result.issues?.length && (
                    <div className="quizify-tool-warning-list mt-3">
                      {result.issues.map((issue) => (
                        <Alert key={issue} variant="warning" className="py-2 mb-2">
                          {issue}
                        </Alert>
                      ))}
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col xs={12}>
          <Card className="quizify-tool-panel">
            <Card.Header className="align-items-center quizify-pdf-preview-header">
              <Card.Header.Title>
                <h4 className="mb-0">{text.resultTitle}</h4>
              </Card.Header.Title>
              {result && (
                <Form className="quizify-pdf-search-form" onSubmit={handlePdfSearchSubmit}>
                  <Form.Control
                    size="sm"
                    type="search"
                    value={pdfSearchQuery}
                    onChange={(event) => {
                      const value = event.target.value;
                      setPdfSearchQuery(value);

                      if (!value.trim()) {
                        setPdfSearchTerm('');
                        setPdfSearchState({ matches: { current: 0, total: 0 }, state: null, term: '' });
                      }
                    }}
                    placeholder={text.pdfSearchPlaceholder}
                    aria-label={text.pdfSearchPlaceholder}
                    disabled={!pdfPreview?.blob || isPreparingPdf}
                  />
                  <Button
                    type="submit"
                    variant="soft-primary"
                    size="sm"
                    disabled={!pdfPreview?.blob || isPreparingPdf}
                  >
                    <FiSearch aria-hidden="true" />
                    <span>{text.pdfSearchAction}</span>
                  </Button>
                  <Button
                    type="button"
                    variant="soft-secondary"
                    size="sm"
                    className="quizify-pdf-search-step"
                    onClick={() => requestPdfSearch('previous')}
                    disabled={!pdfPreview?.blob || isPreparingPdf || !pdfSearchQuery.trim()}
                    aria-label={text.pdfSearchPrevious}
                    title={text.pdfSearchPrevious}
                  >
                    <FiChevronUp aria-hidden="true" />
                  </Button>
                  <Button
                    type="button"
                    variant="soft-secondary"
                    size="sm"
                    className="quizify-pdf-search-step"
                    onClick={() => requestPdfSearch('next')}
                    disabled={!pdfPreview?.blob || isPreparingPdf || !pdfSearchQuery.trim()}
                    aria-label={text.pdfSearchNext}
                    title={text.pdfSearchNext}
                  >
                    <FiChevronDown aria-hidden="true" />
                  </Button>
                  {pdfSearchFeedback && (
                    <span className="quizify-pdf-search-feedback">{pdfSearchFeedback}</span>
                  )}
                </Form>
              )}
            </Card.Header>
            <Card.Body>
              {!result ? (
                <div className="quizify-tool-empty">{text.emptyResult}</div>
              ) : (
                <>
                  {isPreparingPdf && (
                    <div className="quizify-pdf-preview-state">
                      <Spinner animation="border" size="sm" />
                      <span>{text.preparingPdf}</span>
                    </div>
                  )}

                  {!isPreparingPdf && pdfPreview?.blob && (
                    <SearchablePdfPreview
                      onSearchStateChange={setPdfSearchState}
                      pdfPreview={pdfPreview}
                      searchDirection={pdfSearchRequest.direction}
                      searchRequestId={pdfSearchRequest.id}
                      searchTerm={pdfSearchTerm}
                      text={text}
                    />
                  )}

                  {!isPreparingPdf && !pdfPreview?.blob && (
                    <div className="quizify-tool-empty compact">{text.previewUnavailable}</div>
                  )}

                  {!!(relevantCandidates.length || recognizedLines.length) && (
                    <Accordion className="quizify-tool-diagnostics mt-3">
                      {!!relevantCandidates.length && (
                        <Accordion.Item eventKey="extraction-details">
                          <Accordion.Header>
                            <span>{text.diagnosticsTitle}</span>
                            <Badge bg="primary">{selectedCandidate?.label || result.modeLabel || text.selected}</Badge>
                          </Accordion.Header>
                          <Accordion.Body>
                            <div className="table-responsive">
                              <Table className="quizify-tool-table" size="sm">
                                <thead>
                                  <tr>
                                    <th>{text.candidate}</th>
                                    <th>{text.characters}</th>
                                    <th>{text.questions}</th>
                                    <th>{text.artifacts}</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {relevantCandidates.map((candidate) => (
                                    <tr key={candidate.mode}>
                                      <td>
                                        {candidate.label}
                                        {candidate.mode === result.mode && (
                                          <Badge bg="primary" className="ms-2">{text.selected}</Badge>
                                        )}
                                      </td>
                                      <td>{candidate.characters}</td>
                                      <td>{candidate.questionCount || candidate.questionMarkers}</td>
                                      <td>{candidate.artifactCount}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </Accordion.Body>
                        </Accordion.Item>
                      )}

                      {!!recognizedLines.length && (
                        <Accordion.Item eventKey="recognized-text">
                          <Accordion.Header>
                            <span>{text.recognizedTextTitle}</span>
                            <Badge bg="secondary">{recognizedLines.length}</Badge>
                          </Accordion.Header>
                          <Accordion.Body>
                            <div className="quizify-tool-actions justify-content-start mb-3">
                              <Button type="button" variant="soft-primary" size="sm" onClick={handleCopy}>
                                <FiCopy aria-hidden="true" />
                                <span>{text.copy}</span>
                              </Button>
                            </div>
                            <Form.Group className="quizify-repair-search mb-3">
                              <Form.Label>{text.searchLabel}</Form.Label>
                              <Form.Control
                                type="search"
                                value={searchQuery}
                                onChange={(event) => setSearchQuery(event.target.value)}
                                placeholder={text.searchPlaceholder}
                              />
                              <div className="quizify-repair-preview-meta">
                                {searchQuery.trim()
                                  ? text.previewMatches(previewLines.length, recognizedLines.length)
                                  : text.previewRows(Math.min(visibleRecognizedLines, recognizedLines.length), recognizedLines.length)}
                              </div>
                            </Form.Group>
                            <div className="quizify-repair-preview" onScroll={handleRecognizedTextScroll}>
                              {previewLines.length ? (
                                <>
                                  {previewLines.map((line, lineIndex) => (
                                    <div key={`${lineIndex}-${line.slice(0, 24)}`} className="quizify-recognized-line">
                                      {line}
                                    </div>
                                  ))}
                                  {!searchQuery.trim() && visibleRecognizedLines < recognizedLines.length && (
                                    <div className="quizify-recognized-loading">
                                      {text.scrollForMoreRecognizedText}
                                    </div>
                                  )}
                                </>
                              ) : text.noSearchResults}
                            </div>
                          </Accordion.Body>
                        </Accordion.Item>
                      )}
                    </Accordion>
                  )}
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </ToolShell>
  );
};

export default PdfRepairTool;
